import React, { useMemo } from "react";
import { Col } from "syngenta-digital-cropwise-react-ui-kit";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLine,
  VictoryTheme,
  VictoryLabel,
  type DomainPropType,
} from "victory";
import { useTranslation } from "react-i18next";
import { getColorForZone } from "./soilColorUtil";
import { formatTranslation } from "../utils/translationUtils";

interface SoilAnalysisChartProps {
  zoneData: any[];
  zoneThresholds?: number[];
  propertyName: string;
  uom?: string | null;
  distributionStats?: any;
  palette?: string;
  distributionZoneData?: any[];
}

const victoryChartPadding = {
  top: 10,
  left: 50,
  right: 30,
  bottom: 60,
};

export const SoilAnalysisChart: React.FC<SoilAnalysisChartProps> = ({
  zoneData,
  zoneThresholds = [],
  propertyName,
  palette = "DEFAULT",
  distributionZoneData = [],
}) => {
  const { t } = useTranslation();
  // Conversion factor from count to hectares
  const countToHaFactor = 0.01;

  const chartData = useMemo(() => {
    if (!zoneData || zoneData.length === 0) return [];

    return zoneData.map((zone: any, index: number) => {
      let colorZoneIndex = 0;
      if (zoneThresholds && zoneThresholds.length > 2) {
        const zoneCenter =
          Number(zone.min) + (Number(zone.max) - Number(zone.min)) / 2;
        for (let i = 0; i < zoneThresholds.length - 1; i++) {
          if (
            zoneCenter >= zoneThresholds[i] &&
            zoneCenter <= zoneThresholds[i + 1]
          ) {
            colorZoneIndex = i;
            break;
          }
        }
      }
      const color = getColorForZone(
        colorZoneIndex,
        zoneThresholds.length - 1,
        palette
      );
      return {
        x: zone.min,
        y: typeof zone.count === "number" ? zone.count * countToHaFactor : 0,
        fill: color,
      };
    });
  }, [zoneData, zoneThresholds, palette]);

  // Calculate max area for y domain (use count in ha)
  const maxArea = useMemo(() => {
    if (!zoneData || zoneData.length === 0) return 1;
    return Math.max(
      ...zoneData.map((z: any) => (z.count || 0) * countToHaFactor),
      1
    );
  }, [zoneData]);

  // Only draw lines at internal boundaries (not min/max)
  const thresholdLines = useMemo(() => {
    if (!zoneThresholds || zoneThresholds.length <= 2) return null;
    return zoneThresholds.slice(1, -1).map((threshold, index) => (
      <VictoryLine
        key={`threshold-${threshold}-${index}`}
        data={[
          { x: threshold, y: 0 },
          { x: threshold, y: Math.ceil(maxArea * 1.1) },
        ]}
        style={{
          data: {
            stroke: "#C2C7D0",
            strokeWidth: 2,
            strokeOpacity: 1,
            strokeDasharray: "8,8",
            strokeLinecap: "round",
          },
        }}
      />
    ));
  }, [zoneThresholds, maxArea]);

  // Early return AFTER all hooks
  if (!zoneData || zoneData.length === 0) {
    return (
      <div
        style={{
          height: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#F9FAFB",
        }}
      >
        {formatTranslation(t("soil.loadingChartData"))}
      </div>
    );
  }

  // Calculate max area for y domain (use count in ha)
  const domain: DomainPropType = {
    x: [distributionZoneData[0].min, distributionZoneData.at(-1)?.max],
    y: [0, Math.ceil(maxArea * 1.1)],
  };

  return (
    <div style={{ height: "266px", padding: "2px 0px" }}>
      <Col span="auto">
        <span
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#14151C",
            display: "inline-block",
            marginBottom: "16px",
          }}
        >
          {formatTranslation(t("soil.thresholdControl"))}
        </span>
        <VictoryChart
          padding={victoryChartPadding}
          domainPadding={{ x: 15 }}
          domain={domain}
          width={420}
          height={320}
          theme={VictoryTheme.material}
        >
          <VictoryAxis
            dependentAxis
            label="ha"
            style={{
              axis: { stroke: "#E5E7EB" },
              grid: { stroke: "#F3F4F6" },
              tickLabels: { fontSize: 11, fill: "#6B7280" },
              axisLabel: { padding: 40 },
            }}
            tickFormat={(tick: number) =>
              typeof tick === "number" ? tick.toFixed(1) : ""
            }
            axisLabelComponent={
              <VictoryLabel
                id="chart-axis-0-axisLabel-0"
                x={27}
                y={14.26}
                angle={0}
                transform="rotate(0,0,0)"
                style={[
                  {
                    fontSize: 14,
                    fill: "#232630",
                    fontWeight: 400,
                    textAnchor: "middle",
                    fontFamily: "Helvetica Neue, Helvetica, sans-serif",
                    stroke: "transparent",
                    letterSpacing: "normal",
                  },
                ]}
                tspanComponent={<tspan x={26} />}
              />
            }
          />

          <VictoryAxis
            tickValues={zoneData.map((z: any) => z.min)}
            tickFormat={(tick: number) =>
              typeof tick === "number" ? tick.toFixed(2) : ""
            }
            style={{
              axis: { stroke: "#E5E7EB" },
              tickLabels: {
                fontSize: 11,
                fill: "#6B7280",
                padding: 10,
                angle: -40,
                textAnchor: "end",
              },
              axisLabel: { padding: 35 },
            }}
            label={propertyName}
            axisLabelComponent={
              <VictoryLabel
                id="chart-axis-1-axisLabel-0"
                x={404}
                y={257.97}
                style={[
                  {
                    fontSize: 14,
                    fill: "#232630",
                    fontWeight: 400,
                    textAnchor: "middle",
                    fontFamily: "Helvetica Neue, Helvetica, sans-serif",
                    stroke: "transparent",
                    letterSpacing: "normal",
                  },
                ]}
              />
            }
          />

          <VictoryBar
            style={{
              data: {
                fill: (args) => args.datum?.fill,
                fillOpacity: 0.9,
                stroke: "#FFFFFF",
                strokeWidth: 1,
                width: 15,
              },
            }}
            data={chartData}
            barRatio={0.8}
          />
          {thresholdLines}
        </VictoryChart>
      </Col>
    </div>
  );
};
